import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Megaphone, Send } from "lucide-react";

export function HolidayVolunteerDialog({ open, onClose, date, holiday, cleaners }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [sel, setSel] = useState(() => new Set(cleaners.map((c) => c.id)));
  const [needed, setNeeded] = useState("");
  const [note, setNote] = useState("");
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = useMutation({
    mutationFn: () => api.post("/holiday-requests", { date, cleaner_ids: [...sel], needed: needed ? Number(needed) : null, note }),
    onSuccess: () => {
      toast.success(t("Asked {{count}} cleaner(s) — first to say Yes are rostered on", { count: sel.size }));
      qc.invalidateQueries({ queryKey: ["holiday-requests", date] }); qc.invalidateQueries({ queryKey: ["holidays-upcoming"] }); onClose();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const n = needed ? Number(needed) : null;
  const preview = `${t("Can you work {{holiday}} ({{date}})?", { holiday, date: fmtDMY(date) })} ${n ? t("We need {{count}} cleaner(s).", { count: n }) + " " : ""}${note ? note + " " : ""}${t("Please answer Yes or No in the KlenaPro app — the first to say Yes will be rostered on and told; everyone else will be told they're not needed.")}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="holiday-volunteer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-amber-600" /> {t("Ask who can work {{holiday}}", { holiday })}</DialogTitle>
          <DialogDescription>{t("Each selected cleaner gets an in-app message with Yes / No buttons plus an email asking them to open the app and respond. Places are first in, first added: the first to say Yes (up to the number needed) are rostered on automatically; the rest are told the places are filled.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("Ask")}</Label>
            <div className="flex flex-wrap gap-2">
              {cleaners.map((c) => (
                <label key={c.id} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm ${sel.has(c.id) ? "border-amber-500 bg-amber-50" : "bg-card"}`} data-testid={`volunteer-pick-${c.id}`}>
                  <Checkbox checked={sel.has(c.id)} onCheckedChange={() => toggle(c.id)} /> {c.name}
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("Number of cleaners needed (blank = everyone who says Yes)")}</Label>
              <Input type="number" min="1" max="50" value={needed} onChange={(e) => setNeeded(e.target.value)} className="h-9" data-testid="volunteer-needed" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("Extra note (optional)")}</Label>
              <Input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} className="h-9" placeholder={t("e.g. start 9:00, time and a half")} data-testid="volunteer-note" />
            </div>
          </div>
          <div className="rounded-md border bg-secondary/40 p-2.5 text-xs text-muted-foreground" data-testid="volunteer-preview">{preview}</div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="volunteer-cancel">{t("Cancel")}</Button>
          <Button disabled={sel.size === 0 || send.isPending} onClick={() => send.mutate()} className="gap-2" data-testid="volunteer-send">
            {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Send request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HolidayRequestStatus({ date }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: reqs = [] } = useQuery({
    queryKey: ["holiday-requests", date],
    queryFn: async () => (await api.get(`/holiday-requests?date=${date}`)).data,
    refetchInterval: 30000,
  });
  const close = useMutation({
    mutationFn: (id) => api.post(`/holiday-requests/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holiday-requests", date] }),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const hr = reqs[0];
  if (!hr) return null;
  const name = (id) => hr.names?.[id] || id;
  const waiting = hr.cleaner_ids.filter((id) => !hr.responses?.[id]);
  const late = Object.entries(hr.responses || {}).filter(([, r]) => r.answer === "yes_late").map(([id]) => id);
  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="holiday-request-status">
      <span className={`rounded-full px-2 py-0.5 font-semibold ${hr.status === "open" ? "bg-amber-200 text-amber-900" : hr.status === "filled" ? "bg-emerald-200 text-emerald-900" : "bg-slate-200 text-slate-800"}`} data-testid="holiday-request-state">
        {hr.status === "open" ? t("Asking") : hr.status === "filled" ? t("Filled") : t("Closed")}{hr.needed ? ` · ${hr.accepted_ids.length}/${hr.needed}` : ` · ${hr.accepted_ids.length} ${t("yes")}`}
      </span>
      {hr.accepted_ids.length > 0 && <span><b>{t("Yes")}:</b> {hr.accepted_ids.map(name).join(", ")}</span>}
      {(hr.declined_ids || []).length > 0 && <span><b>{t("No")}:</b> {hr.declined_ids.map(name).join(", ")}</span>}
      {late.length > 0 && <span><b>{t("Yes (too late)")}:</b> {late.map(name).join(", ")}</span>}
      {waiting.length > 0 && hr.status === "open" && <span><b>{t("Waiting")}:</b> {waiting.map(name).join(", ")}</span>}
      {hr.email_sent_to?.length > 0 && <span className="text-muted-foreground" data-testid="holiday-request-emailed">{t("Emailed {{count}}", { count: hr.email_sent_to.length })}</span>}
      {hr.status === "open" && (
        <button type="button" onClick={() => close.mutate(hr.id)} className="ml-auto underline-offset-2 hover:underline" data-testid="holiday-request-close">{t("Close request")}</button>
      )}
    </div>
  );
}
